import React, { useState, useEffect } from 'react';
import { 
  Crown, Star, Trophy, Award, Target, Zap,
  BarChart3, TrendingUp, Users, Medal, Shield
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const Top10PlayersByPosition = ({ draftData, loading, error }) => {
  const [positionData, setPositionData] = useState({});
  const [selectedPosition, setSelectedPosition] = useState('QB');
  const [hoveredPlayer, setHoveredPlayer] = useState(null);

  // Position configurations with modern styling
  const positionConfig = {
    QB: { 
      name: 'Quarterback', 
      color: '#3b82f6', 
      gradient: 'from-blue-500 to-blue-600',
      icon: Target,
      bgGradient: 'from-blue-50 to-blue-100'
    },
    RB: { 
      name: 'Running Back', 
      color: '#22c55e', 
      gradient: 'from-green-500 to-green-600',
      icon: Zap,
      bgGradient: 'from-green-50 to-green-100'
    },
    WR: { 
      name: 'Wide Receiver', 
      color: '#f59e0b', 
      gradient: 'from-amber-500 to-amber-600',
      icon: Star,
      bgGradient: 'from-amber-50 to-amber-100'
    },
    TE: { 
      name: 'Tight End', 
      color: '#8b5cf6', 
      gradient: 'from-purple-500 to-purple-600',
      icon: Users,
      bgGradient: 'from-purple-50 to-purple-100'
    },
    K: { 
      name: 'Kicker', 
      color: '#ef4444', 
      gradient: 'from-red-500 to-red-600',
      icon: Award,
      bgGradient: 'from-red-50 to-red-100'
    },
    DEF: { 
      name: 'Defense', 
      color: '#64748b', 
      gradient: 'from-slate-500 to-slate-600',
      icon: Shield,
      bgGradient: 'from-slate-50 to-slate-100'
    }
  };

  // Process data to get top 10 players by position
  useEffect(() => {
    if (!draftData || draftData.length === 0) return;

    const positions = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
    const processedData = {};

    positions.forEach(position => {
      // Filter and group by player for this position
      const positionPlayers = draftData
        .filter(record => record.Position === position)
        .reduce((acc, record) => {
          const player = record.Player?.trim() || '';
          if (!player) return acc;

          if (!acc[player]) {
            acc[player] = {
              playerName: player,
              totalInvestment: 0,
              draftCount: 0,
              managers: new Set(),
              years: new Set()
            };
          }

          acc[player].totalInvestment += record.Cost || 0;
          acc[player].draftCount += 1;
          acc[player].managers.add(record.Manager);
          acc[player].years.add(record.Year);

          return acc;
        }, {});

      // Get top 10 and add ranking
      const top10 = Object.values(positionPlayers)
        .sort((a, b) => b.totalInvestment - a.totalInvestment)
        .slice(0, 10)
        .map((player, index) => ({
          ...player,
          rank: index + 1,
          managersCount: player.managers.size,
          yearsActive: player.years.size,
          yearRange: `${Math.min(...player.years)}-${Math.max(...player.years)}`,
          avgPerDraft: Math.round(player.totalInvestment / player.draftCount)
        }));

      processedData[position] = top10;
    });

    setPositionData(processedData);
  }, [draftData]);

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-3xl p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-blue-200 rounded-xl"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-32 bg-blue-200 rounded-xl"></div>
            ))}
          </div>
          <div className="h-64 bg-blue-200 rounded-xl"></div>
        </div>
      </div>
    );
  }

  if (error || !positionData || Object.keys(positionData).length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-3xl p-8">
        <h3 className="text-lg font-medium text-red-800 mb-2">Unable to Load Position Data</h3>
        <p className="text-red-600">{error || 'No position data available'}</p>
      </div>
    );
  }

  const currentPositionData = positionData[selectedPosition] || [];
  const config = positionConfig[selectedPosition];

  return (
    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-3xl rounded-2xl sm:rounded-3xl overflow-hidden transition-all duration-700 hover:shadow-3xl relative group"
         style={{
           background: `linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(99, 102, 241, 0.06), rgba(255,255,255,0.08))`,
           boxShadow: `0 25px 50px -12px rgba(59, 130, 246, 0.15), 0 8px 32px rgba(99, 102, 241, 0.08)`
         }}>
      
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-gradient-to-br opacity-40 group-hover:opacity-60 transition-opacity duration-700"
           style={{
             background: `linear-gradient(135deg, rgba(59, 130, 246, 0.15), transparent, rgba(99, 102, 241, 0.08))`
           }}></div>
      
      {/* Header Section - Mobile optimized */}
      <div className="p-4 sm:p-6 lg:p-8 relative z-10">
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full mb-3 sm:mb-4 shadow-xl"
               style={{
                 background: `linear-gradient(135deg, ${config.color}, ${config.color}dd)`,
                 boxShadow: `0 25px 40px -10px ${config.color}40`
               }}>
            <Trophy className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-black mb-2 sm:mb-3">
            <span className="bg-gradient-to-r bg-clip-text text-transparent"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${config.color}, ${config.color}dd, #8b5cf6)`
                  }}>
              Elite Players by Position
            </span>
          </h2>
          <p className="text-sm sm:text-base text-gray-700 max-w-2xl mx-auto font-medium px-4 sm:px-0">
            Discover the top 10 highest-paid players at each position in Beasts of Bellevue history
          </p>
        </div>

        {/* Position Selection Grid - Mobile responsive */}
        <div className="grid grid-cols-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 mb-6 sm:mb-8">
          {Object.entries(positionConfig).map(([pos, conf]) => {
            const Icon = conf.icon;
            const isSelected = selectedPosition === pos;
            const playersCount = positionData[pos]?.length || 0;
            
            return (
              <button
                key={pos}
                onClick={() => setSelectedPosition(pos)}
                className={`relative overflow-hidden rounded-xl sm:rounded-2xl p-3 sm:p-4 transition-all duration-300 group/card ${
                  isSelected 
                    ? 'transform scale-105 shadow-2xl' 
                    : 'hover:scale-102 hover:shadow-xl'
                }`}
                style={{
                  background: isSelected 
                    ? `linear-gradient(135deg, ${conf.color}, ${conf.color}dd)` 
                    : `linear-gradient(135deg, ${conf.color}20, ${conf.color}10)`,
                  boxShadow: isSelected 
                    ? `0 20px 40px -10px ${conf.color}40` 
                    : `0 10px 25px -5px ${conf.color}20`
                }}
              >
                <div className="relative z-10">
                  <Icon className={`h-4 w-4 sm:h-6 sm:w-6 mx-auto mb-1 sm:mb-2 ${
                    isSelected ? 'text-white' : 'text-gray-600'
                  }`} />
                  <div className={`text-xs sm:text-sm font-bold ${
                    isSelected ? 'text-white' : 'text-gray-800'
                  }`}>
                    {pos}
                  </div>
                  <div className={`text-xs ${
                    isSelected ? 'text-white/90' : 'text-gray-500'
                  }`}>
                    {playersCount} players
                  </div>
                </div>
                
                {/* Animated background effect */}
                <div className={`absolute inset-0 bg-gradient-to-r opacity-0 group-hover/card:opacity-20 transition-opacity duration-300 ${
                  conf.gradient
                }`}></div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Position Details & Chart - Mobile optimized */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8">
        {/* Position Header - Mobile responsive */}
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className={`p-2 sm:p-3 rounded-xl bg-gradient-to-r ${config.gradient} shadow-lg`}>
                {React.createElement(config.icon, { className: "h-4 w-4 sm:h-6 sm:w-6 text-white" })}
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Top {config.name}s
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  {currentPositionData.length} elite players • Total invested: $
                  {currentPositionData.reduce((sum, p) => sum + p.totalInvestment, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Section - Mobile responsive height */}
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-3 sm:p-4 lg:p-6 shadow-xl border border-white/20">
          <div className="h-[280px] sm:h-[320px] lg:h-[350px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={currentPositionData} 
                margin={{ 
                  top: 20, 
                  right: window.innerWidth < 640 ? 10 : 30, 
                  left: window.innerWidth < 640 ? 10 : 20, 
                  bottom: window.innerWidth < 640 ? 50 : 60 
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis 
                  dataKey="playerName" 
                  angle={-45}
                  textAnchor="end"
                  height={window.innerWidth < 640 ? 40 : 50}
                  stroke="#6b7280"
                  fontSize={window.innerWidth < 640 ? 8 : 10}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#6b7280"
                  fontSize={window.innerWidth < 640 ? 9 : 11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value}`}
                  width={window.innerWidth < 640 ? 35 : 50}
                />
                <Tooltip 
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const player = currentPositionData.find(p => p.playerName === label);
                      if (!player) return null;

                      const isMobile = window.innerWidth < 640;

                      return (
                        <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-xl p-3 sm:p-4 shadow-xl min-w-48 sm:min-w-64">
                          <div className="font-bold text-gray-900 mb-2 sm:mb-3 flex items-center">
                            <Medal className={`h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 ${
                              player.rank === 1 ? 'text-yellow-500' : 
                              player.rank === 2 ? 'text-gray-400' : 
                              player.rank === 3 ? 'text-amber-600' : 'text-gray-500'
                            }`} />
                            <span className="text-sm sm:text-base">#{player.rank} {player.playerName}</span>
                          </div>
                          
                          <div className="space-y-1 sm:space-y-2">
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-xs sm:text-sm">Total Investment:</span>
                              <span className="font-bold text-xs sm:text-sm" style={{ color: config.color }}>
                                ${player.totalInvestment.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-xs sm:text-sm">Times Drafted:</span>
                              <span className="font-semibold text-gray-800 text-xs sm:text-sm">{player.draftCount}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-xs sm:text-sm">Unique Managers:</span>
                              <span className="font-semibold text-gray-800 text-xs sm:text-sm">{player.managersCount}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-xs sm:text-sm">Years Active:</span>
                              <span className="font-semibold text-gray-800 text-xs sm:text-sm">{player.yearRange}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600 text-xs sm:text-sm">Avg per Draft:</span>
                              <span className="font-semibold text-gray-800 text-xs sm:text-sm">${player.avgPerDraft}</span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar 
                  dataKey="totalInvestment" 
                  fill={config.color}
                  radius={[8, 8, 0, 0]}
                  onMouseEnter={(data, index) => setHoveredPlayer(index)}
                  onMouseLeave={() => setHoveredPlayer(null)}
                >
                  {currentPositionData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={
                        hoveredPlayer === index 
                          ? `${config.color}dd`
                          : index < 3 
                          ? `${config.color}`
                          : `${config.color}cc`
                      }
                      style={{
                        filter: hoveredPlayer === index ? 'brightness(1.1)' : 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Elite Podium - Top 3 Highlight Cards - Mobile optimized */}
        {currentPositionData.length >= 3 && (
          <div className="mt-4 sm:mt-6 relative">
            {/* Podium Background Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-50 via-gray-50 to-amber-50 rounded-2xl sm:rounded-3xl opacity-60"></div>
            
            <div className="relative z-10 p-3 sm:p-4 lg:p-6">
              <div className="text-center mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-black text-gray-900 mb-1 sm:mb-2 flex items-center justify-center">
                  <Crown className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2 text-yellow-500" />
                  Elite Podium
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm">The most valuable investments at {config.name}</p>
              </div>
              
              {/* Mobile: Vertical stack, Desktop: Grid */}
              <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-1 md:grid-cols-3 sm:gap-4 max-w-4xl mx-auto">
                {/* Gold Medal - 1st Place */}
                <div className="sm:order-2 md:order-1 relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl sm:rounded-2xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity duration-300"></div>
                  <div className="relative bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-400 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl transform transition-all duration-300 hover:scale-105 hover:-translate-y-2">
                    <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center shadow-lg">
                        <Crown className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="text-center pt-3 sm:pt-4">
                      <div className="text-4xl sm:text-6xl mb-1 sm:mb-2">🥇</div>
                      <h4 className="font-black text-base sm:text-lg text-gray-900 mb-1 truncate" title={currentPositionData[0].playerName}>
                        {currentPositionData[0].playerName}
                      </h4>
                      <div className="text-xl sm:text-3xl font-black text-yellow-600 mb-3 sm:mb-4">
                        ${currentPositionData[0].totalInvestment.toLocaleString()}
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                          <div className="text-yellow-700 text-xs font-semibold uppercase tracking-wide">Drafts</div>
                          <div className="text-sm sm:text-lg font-black text-gray-900">{currentPositionData[0].draftCount}</div>
                        </div>
                        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                          <div className="text-yellow-700 text-xs font-semibold uppercase tracking-wide">Managers</div>
                          <div className="text-sm sm:text-lg font-black text-gray-900">{currentPositionData[0].managersCount}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Silver Medal - 2nd Place */}
                <div className="sm:order-1 md:order-2 relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-400 to-gray-600 rounded-xl sm:rounded-2xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity duration-300"></div>
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-400 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl transform transition-all duration-300 hover:scale-105 hover:-translate-y-2">
                    <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gray-400 to-gray-600 rounded-full flex items-center justify-center shadow-lg">
                        <Medal className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="text-center pt-3 sm:pt-4">
                      <div className="text-4xl sm:text-6xl mb-1 sm:mb-2">🥈</div>
                      <h4 className="font-black text-base sm:text-lg text-gray-900 mb-1 truncate" title={currentPositionData[1].playerName}>
                        {currentPositionData[1].playerName}
                      </h4>
                      <div className="text-xl sm:text-3xl font-black text-gray-600 mb-3 sm:mb-4">
                        ${currentPositionData[1].totalInvestment.toLocaleString()}
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                          <div className="text-gray-700 text-xs font-semibold uppercase tracking-wide">Drafts</div>
                          <div className="text-sm sm:text-lg font-black text-gray-900">{currentPositionData[1].draftCount}</div>
                        </div>
                        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                          <div className="text-gray-700 text-xs font-semibold uppercase tracking-wide">Managers</div>
                          <div className="text-sm sm:text-lg font-black text-gray-900">{currentPositionData[1].managersCount}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bronze Medal - 3rd Place */}
                <div className="sm:order-3 md:order-3 relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-600 to-amber-800 rounded-xl sm:rounded-2xl blur-lg opacity-25 group-hover:opacity-40 transition-opacity duration-300"></div>
                  <div className="relative bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-xl transform transition-all duration-300 hover:scale-105 hover:-translate-y-2">
                    <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-600 to-amber-800 rounded-full flex items-center justify-center shadow-lg">
                        <Award className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                      </div>
                    </div>
                    <div className="text-center pt-3 sm:pt-4">
                      <div className="text-4xl sm:text-6xl mb-1 sm:mb-2">🥉</div>
                      <h4 className="font-black text-base sm:text-lg text-gray-900 mb-1 truncate" title={currentPositionData[2].playerName}>
                        {currentPositionData[2].playerName}
                      </h4>
                      <div className="text-xl sm:text-3xl font-black text-amber-700 mb-3 sm:mb-4">
                        ${currentPositionData[2].totalInvestment.toLocaleString()}
                      </div>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                          <div className="text-amber-800 text-xs font-semibold uppercase tracking-wide">Drafts</div>
                          <div className="text-sm sm:text-lg font-black text-gray-900">{currentPositionData[2].draftCount}</div>
                        </div>
                        <div className="bg-white/70 backdrop-blur-sm rounded-lg p-2 sm:p-3">
                          <div className="text-amber-800 text-xs font-semibold uppercase tracking-wide">Managers</div>
                          <div className="text-sm sm:text-lg font-black text-gray-900">{currentPositionData[2].managersCount}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Top10PlayersByPosition;